import React, { useState, useEffect } from 'react';

import { useAuth } from '@/src/app/context/useAuth';
import { ErrorMessage, Loading } from '@/src/ui';
import { apiRequest } from '@/src/services';
import UnitAccordion from './components/UnitAccordion';
import type { ExamAssignmentDTO, UnitDTO } from './StudentDashboardDTOs';
import ExamAssignmentsTable from './components/ExamAssignmentsTable';
import CollapsiblePanel from '../_shared/CollapsiblePanel';
import { sanitizeGradeShortCode } from '@/src/utils/getUserGrade';
import UserProfileBar from './components/UserProfileBar';
import './StudentDashboard.css';

const thumbnails = import.meta.glob('@/src/assets/thumbnails/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default',
}) as Record<string, string>;

const practiceImgs = import.meta.glob('@/src/assets/practice_platform/*.{png,jpg,jpeg,svg}', {
    eager: true,
    import: 'default',
}) as Record<string, string>;

const StudentDashboard: React.FC = () => {
    document.title = 'Dashboard';
    const { user } = useAuth();
    const isPracticeAllowed = user?.isPracticeAllowed || false;

    const themeCode = user?.alternateGradeColorShortCode || user?.gradeShortCode;
    const currentGradeCode = sanitizeGradeShortCode(themeCode);
    const headerBg = `var(--pm-g${currentGradeCode}-light)`;

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [examAssignments, setExamAssignments] = useState<ExamAssignmentDTO[]>([]);

    const [lessonUnits, setLessonUnits] = useState<UnitDTO[]>([]);
    const [lessonsLoading, setLessonsLoading] = useState<boolean>(false);

    useEffect(() => {
        (async function init() {
            setLoading(true);
            try {
                const studentId = user?.id?.toString();
                if (!studentId) return;

                const res = await apiRequest<ExamAssignmentDTO[]>(
                    'GET',
                    `assessments/examAssignments/student/${studentId}`
                );
                const newAssignments = (res.data || []).filter((assignment) => {
                    const now = new Date();
                    const fromDate = assignment.fromDate ? new Date(assignment.fromDate) : null;
                    const toDate = assignment.toDate ? new Date(assignment.toDate) : null;
                    const isOutOfDate =
                        (!fromDate || now >= fromDate) && (!toDate || now <= toDate);
                    return isOutOfDate || (assignment.attempts && assignment.attempts.length > 0);
                });
                setExamAssignments(newAssignments);
            } catch {
                setError('Failed to load assignments.');
            } finally {
                setLoading(false);
            }
        })();
    }, [user]);

    useEffect(() => {
        if (user?.gradeShortCode) {
            setLessonsLoading(true);
            apiRequest<UnitDTO[]>('GET', `lessons/${user.gradeShortCode}`)
                .then((res) => {
                    setLessonUnits(res.data || []);
                })
                .catch((err) => {
                    console.error('Failed to load lessons', err);
                })
                .finally(() => setLessonsLoading(false));
        }
    }, [user?.gradeShortCode]);

    const gradeCode = user?.gradeShortCode || '';
    const thumbKey = `/src/assets/thumbnails/cover-grade-${gradeCode}.jpg`;
    const thumbSrc = thumbnails[thumbKey];
    const practiceKey = `/src/assets/practice_platform/G${gradeCode}.png`;
    const practiceSrc = practiceImgs[practiceKey];

    if (!thumbSrc) return null;

    const AssignmentsPanel = (
        <CollapsiblePanel
            title="Assignments"
            link="/exams/viewExamAttempts"
            headerTextColor="var(--pm-primary)"
        >
            <ExamAssignmentsTable data={examAssignments} />
        </CollapsiblePanel>
    );

    const PracticePanel = (
        <CollapsiblePanel title="Practice Pathway" headerTextColor="var(--pm-primary)">
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px' }}>
                {practiceSrc && (
                    <a
                        href="https://practice.paradigmmath.com"
                        style={{
                            display: 'flex',
                            width: '90%',
                            flexDirection: 'column',
                            alignItems: 'center',
                        }}
                    >
                        <img
                            src={practiceSrc}
                            alt="Practice Pathway"
                            style={{
                                objectFit: 'contain',
                                borderRadius: '4px',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                transition: 'transform 0.2s',
                            }}
                            onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                            onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                        />
                    </a>
                )}
            </div>
        </CollapsiblePanel>
    );

    const LessonVideosPanel = (
        <CollapsiblePanel title="Support Videos" headerTextColor="var(--pm-primary)">
            <Loading visible={lessonsLoading} />

            {!lessonsLoading && lessonUnits.length === 0 && (
                <div className="p-4 text-center text-gray-500">No lessons available.</div>
            )}

            {!lessonsLoading && lessonUnits.length > 0 && (
                <div id="unitsContainer">
                    {lessonUnits.map((unit) => (
                        <UnitAccordion key={unit.id} unit={unit} />
                    ))}
                </div>
            )}
        </CollapsiblePanel>
    );

    return (
        <>
            <h1 className="page-header">Student Dashboard</h1>

            <Loading visible={loading} />
            <ErrorMessage error={error} />
            <UserProfileBar headerBg={headerBg} />

            <br />
            <div className="flex flex-col lg:flex-row w-full gap-4 lg:gap-4">
                <div className="flex flex-col gap-4 flex-1 min-w-0">
                    <CollapsiblePanel title="Interactive Book" headerTextColor="var(--pm-primary)">
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px' }}>
                            <a
                                href="https://interactivebook.paradigmmath.com/SSOAuth/BridgeLoginFromCookie"
                                style={{
                                    width: '33%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <img
                                    src={thumbSrc}
                                    alt="Teacher eBook"
                                    style={{
                                        objectFit: 'contain',
                                        borderRadius: '4px',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                        transition: 'transform 0.2s',
                                    }}
                                    onMouseOver={(e) =>
                                        (e.currentTarget.style.transform = 'scale(1.05)')
                                    }
                                    onMouseOut={(e) =>
                                        (e.currentTarget.style.transform = 'scale(1)')
                                    }
                                />
                            </a>
                        </div>
                    </CollapsiblePanel>
                    {AssignmentsPanel}
                </div>

                <div className="flex flex-col gap-4 flex-1 min-w-0">
                    {isPracticeAllowed && PracticePanel}
                    {LessonVideosPanel}
                </div>
            </div>
        </>
    );
};

export default StudentDashboard;
